import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { Link } from "react-router-dom";
import { setShowNotification } from "../../actions/notification";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import IconComponent from "@CubeComponents/icons";
import type { ActionableCountRecord } from "@ServerTypes/projects/project.schema";
import { formCategories } from "@Helpers";
import { RootState } from "@Reducers/index";
import type { CustomModuleItem } from "@ServerTypes/portal/portal.schema";
import { untranslated, useTranslation } from "@i18n";

/**
 * One "Take Action" tile. `allow` is `boolean | object` because the portal layout declares a
 * module's `ENABLE` as either a boolean or a nested capability record, and this component only
 * tests it for truthiness. The custom-module tiles pushed below supply optional label/icon/module.
 */
export type ActionTile = {
  id: number;
  count: number;
  label: string | undefined;
  icon: string | undefined;
  module: string | undefined;
  allow: boolean | object | undefined;
  overDueCount: number | undefined;
  next3DaysCount: number | undefined;
  toolContent: string;
};

/**
 * The tiles and the "Take Action" tab badge must be built from the same record, or the badge and the
 * grid disagree whenever a module is enabled/disabled: both go through this and {@link sumEnabledTileCounts}.
 */
export const buildActionTiles = (
  counts: ActionableCountRecord,
  layout: RootState["layout"],
  customModules: CustomModuleItem[],
): ActionTile[] => {
  const modules: ActionTile[] = [
    {
      id: 1,
      count: counts.reviews?.count || 0,
      label: "Reviews",
      icon: "Review",
      module: "reviews",
      allow: layout.REVIEWFLOW?.ENABLE,
      overDueCount: counts.reviews?.overdueCount,
      next3DaysCount: counts.reviews?.next3DaysCount,
      toolContent: `${counts.reviews?.overdueCount || counts.reviews?.next3DaysCount} Review(s) ${
        counts.reviews?.overdueCount ? "exceeded due date" : "approaching due date in 3 day(s)"
      }`,
    },
    {
      id: 4,
      count: counts.issues?.count || 0,
      label: "Issues",
      icon: "IssuesIcon",
      module: "issues",
      allow: layout.ISSUES?.ENABLE,
      overDueCount: counts.issues?.overdueCount,
      // Issues surface the "approaching due date" badge for the next 7 days (other modules use 3 days).
      // The shared badge condition reads `next3DaysCount`, so we feed it the 7-day count here.
      next3DaysCount: counts.issues?.next7DaysCount,
      toolContent: `${counts.issues?.overdueCount || counts.issues?.next7DaysCount} Issue(s) ${
        counts.issues?.overdueCount ? "exceeded due date" : "approaching due date in 7 day(s)"
      }`,
    },
    {
      id: 2,
      count: counts[formCategories.inspection]?.count || 0,
      label: "Inspections",
      icon: "inspectionForm",
      module: formCategories.inspection,
      allow: layout.INSPECTION?.ENABLE,
      overDueCount: counts[formCategories.inspection]?.overdueCount,
      next3DaysCount: counts[formCategories.inspection]?.next3DaysCount,
      toolContent: `${
        counts[formCategories.inspection]?.overdueCount || counts[formCategories.inspection]?.next3DaysCount
      } Inspections ${
        counts[formCategories.inspection]?.overdueCount ? "exceeded due date" : "approaching due date in 3 day(s)"
      }`,
    },
    {
      id: 3,
      count: counts[formCategories.progressUpdates]?.count || 0,
      label: "Progress updates",
      icon: "ProgressUpdates",
      module: formCategories.progressUpdates,
      allow: layout.PROGRESS_UPDATES?.ENABLE,
      overDueCount: counts[formCategories.progressUpdates]?.overdueCount,
      next3DaysCount: counts[formCategories.progressUpdates]?.next3DaysCount,
      toolContent: `${
        counts[formCategories.progressUpdates]?.overdueCount || counts[formCategories.progressUpdates]?.next3DaysCount
      } Progress updates ${
        counts[formCategories.progressUpdates]?.overdueCount ? "exceeded due date" : "approaching due date in 3 day(s)"
      }`,
    },
  ];
  customModules.forEach((module: CustomModuleItem, index: number) => {
    modules.push({
      id: index + 6,
      count: counts[module.resourceType!]?.count || 0,
      label: module.name,
      icon: module.icon,
      module: module.resourceType,
      allow: true,
      overDueCount: counts[module.resourceType!]?.overdueCount,
      next3DaysCount: counts[module.resourceType!]?.next3DaysCount,
      toolContent: `${
        counts[module.resourceType!]?.overdueCount || counts[module.resourceType!]?.next3DaysCount
      } ${module.name} ${
        counts[module.resourceType!]?.overdueCount ? "exceeded due date" : "approaching due date in 3 day(s)"
      }`,
    });
  });
  return modules;
};

/** A tile only contributes to the "Take Action" badge when its module is enabled for the account. */
export const sumEnabledTileCounts = (tiles: ActionTile[]): number =>
  tiles.reduce((total, tile) => (tile.allow ? total + tile.count : total), 0);

interface ActionCardsProps {
  /** Built by the parent so the grid and the tab badge can never report different totals. */
  tiles: ActionTile[];
  loading: boolean;
}

function ActionCards({ tiles, loading }: ActionCardsProps) {
  const { t } = useTranslation();
  const totalCount = sumEnabledTileCounts(tiles);

  const dispatch = useDispatch();
  const closeNotificationModal = () => {
    dispatch(setShowNotification(false));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <IconComponent name="loader-icon" className="animate-spin" width="2rem" height="2rem" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full max-w-3xl p-3">
        <div className="ps-8 py-4 font-semibold">
          {t("Take Action")}{" "}
          <span className="inline-flex items-center p-1 ms-2 text-sm font-bold bg-[#ECF3FE] rounded-xs">
            {totalCount}
          </span>
        </div>
        <hr className="my-2 border-blue-50" />
      </div>
      <div className="flex flex-wrap gap-3 pe-6 ps-8 w-full">
        {tiles.map(
          (action: ActionTile) =>
            action.allow &&
            action.count > 0 && (
              <Link
                to={`/notifications?section=takeAction&module=${action.module}`}
                onClick={closeNotificationModal}
                className="no-underline text-inherit"
                key={action.id}
              >
                <div className="flex items-center justify-between rounded-lg p-4 w-full border cursor-pointer">
                  <div className="flex flex-wrap gap-3 items-center min-w-0">
                    <span>
                      <IconComponent name={action.icon} width="1.5rem" height="1.5rem" />
                    </span>
                    <span className="text-xl font-bold">{action.count}</span>
                    <span className="text-base sm:text-lg font-semibold whitespace-nowrap">{action.label}</span>
                    {/* Both counts are optional on the actionable-count record; the comparison
                        against 0 is the existing undefined-tolerant behaviour. Erased assertions. */}
                    {(action.overDueCount! > 0 || action.next3DaysCount! > 0) && (
                      <ToolTipComponent
                        position="bottom"
                        // i18n GAP: these summaries are assembled from untranslated literals
                        // above, so they go through `untranslated()` rather than `t()`. Fixing this
                        // needs interpolated keys added to public/locales/en.json.
                        content={untranslated(action.toolContent)}
                        className="max-w-50 wrap-break-word"
                      >
                        <span
                          className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs cursor-default ${
                            action.overDueCount ? "bg-red-500" : "bg-yellow-400"
                          }`}
                        >
                          !
                        </span>
                      </ToolTipComponent>
                    )}
                  </div>
                </div>
              </Link>
            ),
        )}
      </div>
    </div>
  );
}

export default ActionCards;
